import { memo, useState, useEffect, useCallback, useRef } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import AddIcon from '@mui/icons-material/Add';
import CircularProgress from '@mui/material/CircularProgress';
import _isEmpty from 'lodash/isEmpty';
//Local files
import { CardItem, Input } from './SecondFlows.styled';
import SecondAnalysesList from 'components/Analyses/SecondAnalyses/List/List';
import ErrorWrapper from 'components/Common/ErrorWrapper/ErrorWrapper';
import useApp from 'hooks/useApp';
import useCustomSelector from 'hooks/useCustomSelector';
import useSuccess from 'hooks/useSuccess';
import useSecondFlows from 'hooks/useSecondFlows';
import useSecondAnalyses from 'hooks/useSecondAnalyses';
import useBlob from 'hooks/useBlob';
import useChecksum from 'hooks/useChecksum';
import { DELETE_SECOND_FLOW } from 'helpers/confirmations';
import { checkEmptyString, checkValidEmail, needToUpdateField } from 'helpers';
import { secondFlowsSelectors } from "features/secondFlows";
import { blobsSelectors } from 'features/blobs';

const SecondFlows = () => {
  const inputRef = useRef(null);
  const { openDeleteDialog } = useApp();
  const { setSuccess } = useSuccess();
  const { getSecondFlow, createSecondFlow, updateSecondFlow } = useSecondFlows();
  const { getSecondAnalyses, createSecondAnalysis, clearLocalSecondAnalyses } = useSecondAnalyses();
  const { processBlob } = useBlob();
  const { processFiles } = useChecksum();
  const id = useCustomSelector(secondFlowsSelectors.getSecondFlowId);
  const secondFlow = useCustomSelector(secondFlowsSelectors.getSecondFlowData);
  const fetching = useCustomSelector(secondFlowsSelectors.secondFlowIsFetching);
  const blobFetching = useCustomSelector(blobsSelectors.blobIsFetching);
  const [email, setEmail] = useState({ value: '', error: '' });
  const [prompt, setPrompt] = useState({ value: '', error: '' });
  const [file, setFile] = useState({ value: null, formattedValue: null });
  const [edit, setEdit] = useState(false);
  const [firstTimeOpened, setFirstTimeOpened] = useState(true);

  const handleEntering = (second_flow) => {
    setEmail({ value: second_flow?.email, error: '' });
    setPrompt({ value: second_flow?.prompt, error: '' });
  };
  const handleExited = () => {
    setEmail({ value: '', error: '' });
    setPrompt({ value: '', error: '' });
    setEdit(false);
  };
  const handleSave = () => {
    let hasError = false;

    if (!checkValidEmail(email.value)) {
      setEmail(prev => ({ ...prev, error: `Email is invalid` }) );
      hasError = true;
    }
    if (!checkEmptyString(prompt.value)) {
      setPrompt(prev => ({ ...prev, error: `Prompt can't be empty` }) );
      hasError = true;
    }
    if (hasError) return;

    const second_flow = {
      ...(needToUpdateField(secondFlow?.email, email.value) && {  email: email.value }),
      ...(needToUpdateField(secondFlow?.prompt, prompt.value) && { prompt: prompt.value }),
    };

    if (!_isEmpty(second_flow)) {
      if (!!id) {
        updateSecondFlow(second_flow)
        .then(({ error }) => {
          if (!error) {
            setSuccess('Flow successfully updated');
          }
        })
        .finally(() => setEdit(false))
      } else {
        createSecondFlow(second_flow)
        .then(({ error }) => {
          if (!error) {
            setSuccess('Flow successfully created');
          }
        })
      }
    } 
  }
  const handleDelete = () => openDeleteDialog({ id: '', type: DELETE_SECOND_FLOW });
  const handleUploadClick = () => inputRef.current.click();
  const handleFileChange = ({ target: { files } }) => {
    processFiles(files, ({ file, checksum }) =>
      processBlob({ file, checksum }, blob => { 
        setFile({ value: blob.id, formattedValue: file });
      })
    );
  };
  const handleCreateSeconAnalysis = () => {
    const second_analysis = { 
      input: file.value
    };
    createSecondAnalysis(second_analysis)
    .then(({ error }) => {
      if (!error) {
        setFile({ value: null, formattedValue: null });
      }
    })
  };
  const fetchSecondAnalyses = useCallback(() => {
    getSecondAnalyses({ offset: 0 });
  }, [getSecondAnalyses]);
  
  useEffect(() => {
    if (!id) {
      handleExited();
      clearLocalSecondAnalyses();
    };
  }, [id, clearLocalSecondAnalyses]);
  useEffect(() => {
    if (firstTimeOpened) {
      getSecondFlow()
      .then(({ payload, error }) => {
        if (!error) {
          const { second_flow } = payload;
          handleEntering(second_flow);
          fetchSecondAnalyses();
        }
      })
      .finally(() => setFirstTimeOpened(false));
    }
  }, [getSecondFlow, fetchSecondAnalyses, firstTimeOpened]);

  return (
    <Box sx={{ flexGrow: 1, overflow: 'auto', p: 5 }}>
      <Container maxWidth={'sm'}>
        <Card sx={{ p: 2 }}>
          <CardItem>
            <Typography sx={{ fontSize: '1.75rem', fontWeight: 600 }}>Configuration</Typography>
            <Box>
              {!id && <Button variant='outlined' disabled={fetching} onClick={handleSave}>Create</Button>}
              {id && !edit && <Button variant='outlined' disabled={fetching} onClick={() => setEdit(true)}>Edit</Button>}
              {id && edit && <Button variant='outlined' disabled={fetching} sx={{ mr: '5px' }} onClick={handleSave}>Update</Button>}
              {id && edit && <Button variant='outlined' sx={{ color: '#E30000', borderColor: '#E30000' }} disabled={fetching} onClick={handleDelete}>Delete</Button>}
            </Box>
          </CardItem>
          <CardItem sx={{ flexDirection: 'column', alignItems: 'flex-start' }}>
            <TextField
              fullWidth
              label='Email'
              disabled={fetching || (!!id && !edit)}
              value={email.value}
              onChange={({ target: { value } }) => setEmail({ value, error: '' })}
              helperText={email.error}
              error={!!email.error}
            />
            <TextField
              fullWidth
              label='Prompt'
              multiline
              minRows={3}
              maxRows={10}
              disabled={fetching || (!!id && !edit)}
              helperText={prompt.error}
              error={!!prompt.error}
              value={prompt.value}
              onChange={({ target: { value } }) => setPrompt({ value, error: '' })}
            />
          </CardItem>
        </Card>
        <Card sx={{ p: 2, mt: 3 }}>
          <CardItem>
            <Typography sx={{ fontSize: '1.75rem', fontWeight: 600 }}>Upload</Typography>
            {!!file.value && <Button variant='outlined' disabled={fetching || blobFetching} sx={{ mr: '5px' }} onClick={handleCreateSeconAnalysis}>Send</Button>}
          </CardItem>
          <Box display='flex' justifyContent='center' alignItems='center'>
            <Button 
              color='primary' 
              startIcon={<AddIcon />} 
              endIcon={blobFetching && <CircularProgress size={20} color='inherit' />}
              disabled={ blobFetching || !id} 
              onClick={handleUploadClick}
            >
              PDF FILE
            </Button>
            {!!file.value && <Typography variant='body2' noWrap>{file?.formattedValue?.name}</Typography>}
            <Input
                ref={inputRef}
                accept=".pdf"
                id="documents"
                type="file"
                onClick={(event) => event.target.value = ''}
                onChange={handleFileChange}
            />
          </Box>
        </Card>  
        <Card sx={{ p: 2, mt: 3 }}>
          <Typography sx={{ fontSize: '1.75rem', fontWeight: 600 }}>History</Typography>
          <SecondAnalysesList />
        </Card>    
      </Container>
    </Box>
  );
};

const WrappedComponent = props => {
  return (
    <ErrorWrapper>
      <SecondFlows {...props} />
    </ErrorWrapper>
  );
};

export default memo(WrappedComponent);